import { useCallback, useState } from "react";

import { profileEndpoints } from "../api/endpoints";
import { ProfilePayload } from "../api/types";
import { STORAGE_KEY_PREFIX, useAuth } from "@/modules/auth";

type Status = "idle" | "loading" | "success" | "error";

export const useProfile = () => {
    const { profileData, setProfileData, userPreferences, setUserPreferences } = useAuth();
    const [status, setStatus] = useState<Status>("idle");
    const [error, setError] = useState<string | null>(null);

    const resetProfileMutation = useCallback(() => {
        setError(null);
        setStatus("idle");
    }, []);

    const updateProfile = useCallback(async (userId: string, updates: Partial<ProfilePayload>) => {
        setStatus("loading");
        setError(null);

        try {
            const response = await profileEndpoints.updateProfile({
                userID: userId,
                ...updates,
            } as Partial<ProfilePayload>);
            
            if (response?.success) {
                const profileStoraged = localStorage.getItem(`${STORAGE_KEY_PREFIX}.profileData`);
                const userPreferencesStoraged = localStorage.getItem(`${STORAGE_KEY_PREFIX}.userPreferences`);

                if (response.profileData) {
                    if (!profileStoraged || profileStoraged === "undefined") {
                        throw new Error("Profile data not found");
                    }
                    const currentProfile = JSON.parse(profileStoraged ?? "{}");
                    const newProfileData = { ...currentProfile, ...(response.profileData ? response.profileData : {}) };
                    localStorage.setItem(`${STORAGE_KEY_PREFIX}.profileData`, JSON.stringify(newProfileData));
                    setProfileData(newProfileData);
                }

                if (response.userPreferences) {
                    if (!userPreferencesStoraged || userPreferencesStoraged === "undefined") {
                        throw new Error("User preferences not found");
                    }
                    const currentUserPreferences = JSON.parse(userPreferencesStoraged ?? "{}");
                    const newUserPreferences = { ...currentUserPreferences, ...(response.userPreferences ? response.userPreferences : {}) };
                    localStorage.setItem(`${STORAGE_KEY_PREFIX}.userPreferences`, JSON.stringify(newUserPreferences));
                    setUserPreferences(newUserPreferences);
                }
                
                setStatus("success");
                return { profileData: response.profileData, userPreferences: response.userPreferences };
            }

            const message = "Failed to update profile";
            setError(message);
            setStatus("error");
            throw new Error(message);
        } catch (err) {
            console.error("Failed to update profile", err);
            const message = err instanceof Error ? err.message : "Failed to update profile";
            setError(message);
            setStatus("error");
            throw err instanceof Error ? err : new Error(message);
        }
    }, []);

    return {
        profileData,
        userPreferences,
        status,
        error,
        updateProfile,
        resetProfileMutation,
    };
};
